<template>
  <div class="card" :class="{'expand-card': state.currentTab === 1 || state.currentTab === 3}">
    <div class="parameters-header">
      <TabsNavigation :tabs="tabsTitle" @changeTab="changeTab" :currentTab="state.currentTab" />
      <Button class="green" :label="$t('app.campaign.settings.saveButton')" @click="save"></Button>
    </div>
    <transition mode="out-in" name="fade">
      <div class="tab-body">
        <InformationsTab :class="{hidden: state.currentTab !== 0}" ref="informationTab" v-model="state.data" @changeInvitation="(value) => (state.data.enabled = value)" @toggleError="(val) => (state.informationsError = val)" />
        <NewPonderation style="height: 400px;" :class="{hidden: state.currentTab !== 1}" ref="ponderationTab" v-model="state.data" :disableIgnored="isCampaignV3OrMore(state.data as Campaign)"></NewPonderation>
        <CampaignLink :class="{hidden: state.currentTab !== 2}" ref="campaignLinkTab" :link-activated="state.linkActivated" :public-invitation="publicInvitation" @link-updated="state.linkActivated = $event" @auto-activate="state.manualActivation = $event"></CampaignLink>
        <PreviewTab :class="{hidden: state.currentTab !== 3}" :data="state.data" @changeImage="(event) => (state.data.branding.background_image_id = event)" @changePublicName="(event) => (state.data.campaign.headline = event)" @changeJobTitle="(event) => (state.data.campaign.job_title = event)" @changeJobDescription="(event) => (state.data.campaign.tagline = event)" @toggleError="(val) => (state.cardError = val)" />
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>

.hidden {
  display: none;
}

.tab-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.parameters-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  .tabs-navigation {
    width: unset;
  }
}

.expand-card {
  flex: 1;
}

</style>

<script setup lang="ts">
import { useToast } from "vue-toastification";
import { cloneDeep } from "lodash";

import InformationsTab from "@/views/app/campaigns/singleCampaign/partials/parametersTab/Informations.vue";
import PreviewTab from "@/views/app/campaigns/singleCampaign/partials/parametersTab/Preview.vue";
import CampaignLink from "../parametersTab/CampaignLink.vue"
import NewPonderation from "../../../newCampaign/partials/NewPonderation.vue";
import { Campaign } from "@/types/campaign";
import { useCampaign, useEditCampaign } from "@/queries/campaigns.query";
import { useCampaignPublicInvitation, useDisablePublicInvitation, useEnablePublicInvitation } from "@/queries/invitation.query.js";

const route = useRoute();
const appStore = useAppStore();
const { t } = useI18n();
const toast = useToast();
const { isCampaignV3OrMore } = useSkillsStore();

const { data: campaign, isPending: isCampaignPending, dataUpdatedAt } = useCampaign();
const { data: publicInvitation, isPending: isPublicInvitationPending } = useCampaignPublicInvitation();
const { mutate: editCampaign } = useEditCampaign();
const { mutate: enablePublicInvitation } = useEnablePublicInvitation();
const { mutate: disablePublicInvitation } = useDisablePublicInvitation();

const state = reactive({
  currentTab: appStore.campaignParametersTab,
  data: {
    id: campaign.value.id,
    campaign: { ...campaign.value.campaign },
    branding: { ...campaign.value.branding },
    assessment: cloneDeep(campaign.value.assessment),
    campaign_version: campaign.value.campaign_version,
    variant: campaign.value.variant,
  } as Partial<Campaign>,
  cardError: false,
  informationsError: false,
  linkActivated: false,
  manualActivation: true,
});

const tabsTitle = computed(() => {
  const array = [t("app.campaign.settings.tabs.info.tabName"), t("app.campaign.settings.tabs.ponderation.tabName"), t("app.campaign.settings.tabs.campaignLink.tabName"),t("app.campaign.settings.tabs.preview.tabName")];
  return array;
});

function changeTab(value) {
  state.currentTab = value;
  appStore.campaignParametersTab = value;
}

function save() {
  if (campaign.value.campaign.internal_name !== state.data.campaign.internal_name) {
    campaign.value.campaign.internal_name = state.data.campaign.internal_name;
  }

  console.log("Saving campaign with data:", state.data);
  editCampaign({
    context: { organisationId: route.params.companyId as string, spaceId: route.params.companyId as string, campaignId: route.params.campaignId as string },
    campaign: state.data,
    auto_activate: !state.manualActivation,
  });

  if (!state.linkActivated && publicInvitation?.value?.id) {
    disablePublicInvitation({ invitationId: publicInvitation?.value?.id });
  } else if (state.linkActivated && !publicInvitation?.value?.id) {
    enablePublicInvitation({ campaignId: route.params.campaignId as string });
  }
  toast.success(t('app.campaign.settings.toast.save'));
}

watch(dataUpdatedAt, () => {
  state.data = {
    id: campaign.value.id,
    campaign: { ...campaign.value.campaign },
    branding: { ...campaign.value.branding },
    assessment: cloneDeep(campaign.value.assessment),
    campaign_version: campaign.value.campaign_version,
    variant: campaign.value.variant,
  } as Partial<Campaign>;
});

watch(isPublicInvitationPending, () => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.manualActivation = publicInvitation?.value?.requires_approval;
});

onMounted(() => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.manualActivation = publicInvitation?.value?.requires_approval;
});
</script>